import { KeyboardEvent, useEffect, useRef } from 'react'
import S from '../locales/confirm-dialog.json'
import { tr, type Lang } from '../lib/i18n'

interface ConfirmDialogProps {
  title: string
  /** The ask, filled, in the till's language (R537: one language at a time). */
  line: string
  /** The till's language. */
  lang: Lang
  /** True once Approve was pressed and the write is on its way. */
  sending: boolean
  onApprove: () => void
  onCancel: () => void
  /** Where focus goes back to when the dialog closes, if it is still on the page. */
  returnFocus: HTMLElement | null
  /** Used when returnFocus has left the page (the row was sent and removed). */
  fallbackFocusId: string
}

// ux3 (f): the in-app ask that replaces window.confirm on Final check. A modal alertdialog:
// focus starts on Cancel (the safe answer for money), Tab stays inside, Escape cancels,
// and focus goes back where it came from on close. Four button states: default,
// hover/focus-visible, disabled, and pending ("Approving...") while the write is sent.
export function ConfirmDialog({
  title,
  line,
  lang,
  sending,
  onApprove,
  onCancel,
  returnFocus,
  fallbackFocusId,
}: ConfirmDialogProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const approveRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    cancelRef.current?.focus()
    return () => {
      if (returnFocus && returnFocus.isConnected && !(returnFocus as HTMLButtonElement).disabled) {
        returnFocus.focus()
      } else {
        document.getElementById(fallbackFocusId)?.focus()
      }
    }
    // Mount and unmount only: the dialog opens once per ask.
  }, [])

  useEffect(() => {
    // The buttons are disabled while sending, so keep focus inside the dialog.
    if (sending) panelRef.current?.focus()
  }, [sending])

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault()
      if (!sending) onCancel()
      return
    }
    if (e.key !== 'Tab') return
    e.preventDefault()
    const items = [cancelRef.current, approveRef.current].filter(
      (el): el is HTMLButtonElement => el !== null && !el.disabled,
    )
    if (items.length === 0) return
    const at = items.indexOf(document.activeElement as HTMLButtonElement)
    const next = e.shiftKey ? (at <= 0 ? items.length - 1 : at - 1) : at === items.length - 1 ? 0 : at + 1
    items[next].focus()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-fleet-900/60 px-4 py-6 sm:items-center"
      onKeyDown={handleKeyDown}
    >
      <div
        ref={panelRef}
        id="final-check-confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="final-check-confirm-title"
        aria-describedby={`final-check-confirm-${lang}`}
        aria-busy={sending}
        tabIndex={-1}
        className="w-full max-w-sm space-y-4 rounded-xl bg-white p-5 font-body text-fleet-800 shadow-xl focus:outline-none"
      >
        <h2 id="final-check-confirm-title" className="font-condensed text-xl font-bold uppercase tracking-wide">
          {title}
        </h2>
        <p id={`final-check-confirm-${lang}`} lang={lang} className="text-base font-semibold">
          {line}
        </p>
        <div className="flex gap-3">
          <button
            ref={cancelRef}
            id="final-check-confirm-cancel"
            type="button"
            disabled={sending}
            onClick={onCancel}
            className="flex-1 min-h-tap rounded-lg border-2 border-fleet-300 px-4 py-2 font-semibold text-fleet-700 hover:bg-fleet-100 disabled:border-fleet-200 disabled:text-fleet-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
          >
            {tr(S.cancel, lang)}
          </button>
          <button
            ref={approveRef}
            id="final-check-confirm-approve"
            type="button"
            disabled={sending}
            onClick={onApprove}
            className="flex-1 min-h-tap rounded-lg bg-amber-500 px-4 py-2 font-semibold text-white hover:bg-amber-600 disabled:bg-fleet-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
          >
            {sending ? tr(S.approving, lang) : tr(S.approve, lang)}
          </button>
        </div>
      </div>
    </div>
  )
}
